/**
 * The SOURCE's identity — publisher, channel, artist, company, journal, court,
 * wiki edition, merchant — as opposed to the item's own name.
 *
 * Three densities, one component, because the difference between them is how
 * much of the same record is shown rather than three different designs. Every
 * optional slot is ABSENT when the data has none: an empty placeholder where a
 * verified glyph or a handle would go tells a reader the source failed a check
 * it was never given.
 *
 * The density is a DISCRIMINANT, not a style flag. Each one declares the slots
 * it draws and refuses the rest, because the alternative is what this component
 * used to do: `Inline` renders mark plus name, so a caller that handed it a
 * `secondary` got no error and no output, and the descriptor left the surface
 * silently. A slot a density cannot draw is a compile error now, and the reader
 * never loses a line of the record to a prop that was quietly ignored.
 */
"use client";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { useCresultcardNestedRadius } from "./cresultcard.radius";
import {
  CRC_MARK_PIXELS,
  CRC_TEXT,
  CRC_TEXT_FAINT,
  CRC_TEXT_MUTED,
  CRC_TILE_BACKGROUND_COLOR,
  CRC_TILE_SHADOW,
  CRC_TRUNCATE_STYLE,
  CRC_TYPE_STYLE,
  crcTextClampStyle,
} from "./cresultcard.tokens";
import {
  CresultcardIdentityBandDensity,
  CresultcardMarkShape,
  CresultcardMarkSize,
  CresultcardTextStep,
  CresultcardTitleLines,
} from "./cresultcard.types";
import { CresultcardIdentityMark } from "./CresultcardIdentityMark";
import { CresultcardStatRow } from "./CresultcardStatRow";
import type { CresultcardStatSpec } from "./cresultcard.types";

const MARK_SIZE_BY_DENSITY: Record<
  CresultcardIdentityBandDensity,
  CresultcardMarkSize
> = {
  [CresultcardIdentityBandDensity.Inline]: CresultcardMarkSize.Row,
  [CresultcardIdentityBandDensity.Byline]: CresultcardMarkSize.Card,
  [CresultcardIdentityBandDensity.Banner]: CresultcardMarkSize.Hero,
};

/**
 * The Banner's own geometry.
 *
 * §2.3 describes Banner as an eyebrow STRIP with the mark OVERLAPPING it, and
 * the component drew neither — the eyebrow was one more line inside the copy
 * column and the mark sat beside it, so `Banner` was `Byline` with a bigger
 * mark. A pillar that wanted the described band had to compose it by hand from
 * `IdentityMark` + `Tile` + `StatRow`, which is three pillars away from being a
 * shared component. The strip is drawn whether or not there is an eyebrow: it is
 * the surface the mark overlaps, and a band with no strip has nothing to
 * overlap. The eyebrow itself stays absent when the data has none.
 *
 * ─── THE OVERLAP BELONGS TO THE MARK, AND ONLY TO THE MARK ─────────────────
 *
 * The −28px used to be a `marginBlockStart` on the whole identity ROW — mark
 * and copy together — under `align-items: flex-end`, which made the overlap a
 * function of how tall the copy happened to be instead of a decision:
 *
 *   - copy taller than the 56px mark (a name + a three-line bio + a stat row is
 *     ~85px on the `r/LocalLLaMA` community card) → the copy's top landed at
 *     y 12, twenty-eight pixels INSIDE the 40px strip, and the name painted
 *     straight through the eyebrow's glyphs, which sit at y 15.5–24.5;
 *   - and the same line inverted the mark: bottom-aligned against tall copy it
 *     dropped to y 40.6, below the strip entirely, leaving only its 3px ring to
 *     cut across the strip's bottom hairline.
 *
 * So the overlap is now declared, in pixels, and applies to the mark alone:
 *
 *   strip     40px tall, its own surface, 12px inset at the trailing edge.
 *   mark      56px (Hero), hanging exactly 28px — half of itself — into the
 *             strip: mark top y 12, foot y 68, 12px of strip left above it and
 *             28px of mark standing below it. This is the ONE declared pair,
 *             the mark is in front, and the strip is a surface rather than
 *             content, so nothing of it is lost by being covered.
 *   ring      3px, painted as a `box-shadow` and therefore carrying NO layout
 *             box, so its extent is declared here: top y 9 (nine pixels inside
 *             the strip's top edge, never crossing it), leading edge x 9 (three
 *             of the 12px inset spent, and the band itself sits inside
 *             `CresultcardDetail`'s COLUMN_PAD 14, so x 23 off the plate),
 *             foot y 71 — which `BANNER_PROFILE_MIN_HEIGHT` reserves so the ring
 *             can never reach past the band's own bottom edge either.
 *   copy      starts at y 40 — the strip's bottom edge — at EVERY copy height,
 *             short or tall. It is the repair: the copy column and the eyebrow
 *             can no longer occupy one pixel between them.
 *   eyebrow   indented to x 80 (12 inset + 56 mark + 12 gap), which is the copy
 *             column's own leading edge, so eyebrow, name, handle, bio and
 *             stats share one left edge and the mark's full 71px extent stops
 *             9px short of the eyebrow's first glyph. Without it the mark tile
 *             sits on top of the eyebrow's opening characters at every copy
 *             height — the collision the old geometry hid only on the cards
 *             whose copy happened to run tall.
 */
const BANNER_STRIP_HEIGHT = 40;
const BANNER_STRIP_INSET = 12;
const BANNER_COLUMN_GAP = 12;
const BANNER_MARK_PIXELS = CRC_MARK_PIXELS[CresultcardMarkSize.Hero];
/** Half the mark hangs below the strip's edge, which is what "overlapping" is. */
const BANNER_MARK_OVERLAP = BANNER_MARK_PIXELS / 2;
/** The mark reads as sitting ON the strip rather than punched through it. */
const BANNER_MARK_RING = 3;
const BANNER_MARK_RING_COLOR = "rgba(4,16,18,0.92)";
/**
 * The gutter the mark owns, so the eyebrow starts where the copy starts rather
 * than underneath the tile that hangs over it.
 */
const BANNER_EYEBROW_INSET =
  BANNER_STRIP_INSET + BANNER_MARK_PIXELS + BANNER_COLUMN_GAP;
/**
 * What the identity row reserves below the strip: the 28px of mark standing
 * clear of it, plus the ring's 3px foot. A band whose copy is shorter than that
 * still cannot let the ring hang out of its own box.
 */
const BANNER_PROFILE_MIN_HEIGHT =
  BANNER_MARK_PIXELS - BANNER_MARK_OVERLAP + BANNER_MARK_RING;

interface CresultcardIdentityBandBase {
  readonly name: string;
  readonly markSrc?: string;
  /**
   * The plate to draw where `markSrc` is absent or dies — the member
   * placeholder image on a person-shaped identity. Supplied, it REPLACES the
   * monogram in both states rather than sitting behind it.
   */
  readonly markFallbackSrc?: string;
  /** The monogram, where the derivation from `name` cannot reach it. */
  readonly markMonogram?: string;
  readonly markShape?: CresultcardMarkShape;
}

/** Mark plus name. The density IS the absence of everything else. */
interface CresultcardIdentityBandInlineProps extends CresultcardIdentityBandBase {
  readonly density: CresultcardIdentityBandDensity.Inline;
  readonly secondary?: never;
  readonly secondaryLines?: never;
  readonly age?: never;
  readonly eyebrow?: never;
  readonly verified?: never;
  readonly handle?: never;
  readonly stats?: never;
}

/** Mark, name, what the source IS, and how old the record is. */
interface CresultcardIdentityBandBylineProps extends CresultcardIdentityBandBase {
  readonly density: CresultcardIdentityBandDensity.Byline;
  readonly secondary?: string;
  readonly secondaryLines?: CresultcardTitleLines;
  /** A timestamp, never a breathing dot. */
  readonly age?: string;
  readonly eyebrow?: never;
  readonly verified?: never;
  readonly handle?: never;
  readonly stats?: never;
}

/** The full profile band: eyebrow strip, overlapping mark, handle, stats. */
interface CresultcardIdentityBandBannerProps extends CresultcardIdentityBandBase {
  readonly density: CresultcardIdentityBandDensity.Banner;
  readonly secondary?: string;
  readonly secondaryLines?: CresultcardTitleLines;
  /** The strip above the name. */
  readonly eyebrow?: string;
  /** The vendor's own verification, rendered only when it exists. */
  readonly verified?: ReactNode;
  readonly handle?: string;
  readonly stats?: readonly CresultcardStatSpec[];
  readonly age?: never;
}

export type CresultcardIdentityBandProps =
  | CresultcardIdentityBandInlineProps
  | CresultcardIdentityBandBylineProps
  | CresultcardIdentityBandBannerProps;

export function CresultcardIdentityBand({
  density,
  name,
  markSrc,
  markFallbackSrc,
  markMonogram,
  markShape = CresultcardMarkShape.Square,
  secondary,
  secondaryLines = CresultcardTitleLines.One,
  age,
  eyebrow,
  verified,
  handle,
  stats,
}: CresultcardIdentityBandProps): ReactElement {
  const isBanner = density === CresultcardIdentityBandDensity.Banner;
  const stripRadius = useCresultcardNestedRadius();

  const copy = (
    <div
      data-el="Cresultcard/IdentityBand/Copy"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        minWidth: 0,
      }}
    >
      <span
        style={{
          ...CRC_TRUNCATE_STYLE,
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontSize: isBanner ? 14 : 11.5,
          fontWeight: 720,
          letterSpacing: "-0.012em",
          lineHeight: 1.15,
          color: CRC_TEXT,
        }}
      >
        {name}
        {verified}
      </span>
      {handle != null && (
        <span
          style={{
            ...CRC_TRUNCATE_STYLE,
            fontSize: 9.5,
            fontWeight: 560,
            color: CRC_TEXT_FAINT,
          }}
        >
          {handle}
        </span>
      )}
      {secondary != null && (
        <span
          style={{
            ...crcTextClampStyle({ lines: secondaryLines }),
            minWidth: 0,
            fontSize: 10,
            fontWeight: 520,
            lineHeight: 1.3,
            color: CRC_TEXT_MUTED,
          }}
        >
          {secondary}
        </span>
      )}
      {age != null && (
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 560,
            fontVariantNumeric: "tabular-nums",
            color: CRC_TEXT_FAINT,
          }}
        >
          {age}
        </span>
      )}
      {stats != null && stats.length > 0 && (
        <CresultcardStatRow
          stats={stats}
        />
      )}
    </div>
  );

  const mark = (
    <CresultcardIdentityMark
      fallbackSrc={markFallbackSrc}
      monogram={markMonogram}
      name={name}
      shape={markShape}
      size={MARK_SIZE_BY_DENSITY[density]}
      src={markSrc}
    />
  );

  if (!isBanner) {
    return (
      <div
        data-el="Cresultcard/IdentityBand/Root"
        data-crc-band-density={density}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          minWidth: 0,
        }}
      >
        {mark}
        {copy}
      </div>
    );
  }

  const markRingRadius =
    markShape === CresultcardMarkShape.Circle ? "50%" : stripRadius;

  const stripStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    height: BANNER_STRIP_HEIGHT,
    paddingInlineStart: BANNER_EYEBROW_INSET,
    paddingInlineEnd: BANNER_STRIP_INSET,
    borderRadius: stripRadius,
    backgroundColor: CRC_TILE_BACKGROUND_COLOR,
    boxShadow: CRC_TILE_SHADOW,
  };

  return (
    <div
      data-el="Cresultcard/IdentityBand/Root"
      data-crc-band-density={density}
      style={{ display: "flex", flexDirection: "column", minWidth: 0 }}
    >
      <div
        data-el="Cresultcard/IdentityBand/Header"
        style={stripStyle}
      >
        {eyebrow != null && (
          <span
            style={{
              ...CRC_TYPE_STYLE[CresultcardTextStep.Eyebrow],
              ...CRC_TRUNCATE_STYLE,
            }}
          >
            {eyebrow}
          </span>
        )}
      </div>
      <div
        data-el="Cresultcard/IdentityBand/Surface"
        style={{
          display: "flex",
          // The row itself begins BELOW the strip, so the copy column's top is
          // the strip's bottom edge whatever the copy turns out to weigh.
          alignItems: "flex-start",
          gap: BANNER_COLUMN_GAP,
          minWidth: 0,
          minBlockSize: BANNER_PROFILE_MIN_HEIGHT,
          paddingInline: BANNER_STRIP_INSET,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            flex: "0 0 auto",
            // The declared overlap, carried by the mark alone: its margin box
            // is 28px tall where its border box is 56px, so it hangs half of
            // itself into the strip and the row below it is unaffected.
            marginBlockStart: -BANNER_MARK_OVERLAP,
            borderRadius: markRingRadius,
            boxShadow: `0 0 0 ${String(BANNER_MARK_RING)}px ${BANNER_MARK_RING_COLOR}`,
          }}
        >
          {mark}
        </span>
        {copy}
      </div>
    </div>
  );
}
